// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Box, Button, Group, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Communication, GuidanceResponse, Provenance } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  INLAB_REFERRAL_MODULE_URI,
  PATIENT_REFERRAL_STATUS,
  PATIENT_SYSTEM_STATUS,
  PERFORMER_EXTENSION_URL,
  POLARIS_REFERRAL_STATUS_TAG_SYSTEM,
  POLARIS_SYSTEM_STATUS_TAG_SYSTEM,
  replaceTag,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { DeniedEntry } from '../../hooks/useDeniedQueue';
import { useRole } from '../../hooks/useRole';
import { hasPermission } from '../../utils/permissions';
import { withTenantScope } from '../../utils/tenant';

const GR_STATUS_MAP: Record<string, GuidanceResponse['status']> = {
  'n/a': 'data-requested',
  'ready-to-refer': 'data-required',
  referred: 'success',
};

const REFERRAL_STATUS_OPTIONS = [
  { value: PATIENT_REFERRAL_STATUS.na, label: 'N/A' },
  { value: PATIENT_REFERRAL_STATUS.readyToRefer, label: 'Ready to Refer' },
  { value: PATIENT_REFERRAL_STATUS.referred, label: 'Referred' },
];

export interface ReferralOutModalProps {
  opened: boolean;
  onClose: () => void;
  entry: DeniedEntry | null;
  onSaved: () => void;
  existingResponse?: GuidanceResponse;
}

export function ReferralOutModal({
  opened,
  onClose,
  entry,
  onSaved,
  existingResponse,
}: ReferralOutModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role, isAdmin } = useRole();
  const canRefer = hasPermission({ role, permission: 'refer-out-patient', isAdmin });

  const responseStatus = existingResponse
    ? Object.entries(GR_STATUS_MAP).find(([, value]) => value === existingResponse.status)?.[0]
    : undefined;
  const [status, setStatus] = useState<string>(responseStatus ?? entry?.referralStatus ?? PATIENT_REFERRAL_STATUS.na);
  const [notes, setNotes] = useState<string>(
    existingResponse?.note?.map((note) => note.text).join('\n') ?? entry?.referralNotes ?? ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [eventNote, setEventNote] = useState('');
  const [contactMethod, setContactMethod] = useState('phone');
  const [eventDateTime, setEventDateTime] = useState(() => new Date().toISOString().slice(0, 16));

  useEffect(() => {
    const nextStatus = existingResponse
      ? Object.entries(GR_STATUS_MAP).find(([, value]) => value === existingResponse.status)?.[0]
      : undefined;
    setStatus(nextStatus ?? entry?.referralStatus ?? PATIENT_REFERRAL_STATUS.na);
    setNotes(existingResponse?.note?.map((note) => note.text).join('\n') ?? entry?.referralNotes ?? '');
    setEventNote('');
    setContactMethod('phone');
    setEventDateTime(new Date().toISOString().slice(0, 16));
  }, [entry, existingResponse]);

  async function handleSave(): Promise<void> {
    if (!entry?.patientId) {
      return;
    }
    setSubmitting(true);
    try {
      if (!eventNote.trim()) {
        showNotification({ color: 'red', message: 'Describe what happened before saving the update.' });
        setSubmitting(false);
        return;
      }
      const patient = await medplum.readResource('Patient', entry.patientId);
      let updatedTags = replaceTag(patient.meta?.tag ?? [], {
        system: POLARIS_REFERRAL_STATUS_TAG_SYSTEM,
        code: status,
      });
      if (status === PATIENT_REFERRAL_STATUS.referred) {
        updatedTags = replaceTag(updatedTags, {
          system: POLARIS_SYSTEM_STATUS_TAG_SYSTEM,
          code: PATIENT_SYSTEM_STATUS.inactive,
        });
      }
      const guidanceResponse: GuidanceResponse = withTenantScope({
        ...(existingResponse ?? {}),
        resourceType: 'GuidanceResponse',
        moduleUri: INLAB_REFERRAL_MODULE_URI,
        status: GR_STATUS_MAP[status] ?? 'in-progress',
        subject: { reference: `Patient/${entry.patientId}` },
        occurrenceDateTime: new Date().toISOString(),
        note: [{ text: notes.trim() ? `${notes.trim()}\n\n${eventNote.trim()}` : eventNote.trim() }],
        extension: profile ? [{ url: PERFORMER_EXTENSION_URL, valueReference: createReference(profile) }] : undefined,
      });
      await medplum.executeBatch({
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [
          {
            resource: { ...patient, meta: { ...patient.meta, tag: updatedTags } },
            request: { method: 'PUT', url: `Patient/${entry.patientId}` },
          },
          {
            resource: guidanceResponse,
            request: existingResponse?.id
              ? { method: 'PUT', url: `GuidanceResponse/${existingResponse.id}` }
              : { method: 'POST', url: 'GuidanceResponse' },
          },
        ],
      });
      await medplum.createResource<Communication>(
        withTenantScope({
          resourceType: 'Communication',
          status: 'completed',
          subject: { reference: `Patient/${entry.patientId}`, display: entry.patientName },
          sent: new Date(eventDateTime).toISOString(),
          sender: profile ? createReference(profile) : undefined,
          payload: [{ contentString: `${contactMethod}: ${eventNote.trim()}` }],
        })
      );
      if (existingResponse?.id) {
        await medplum.createResource<Provenance>(
          withTenantScope({
            resourceType: 'Provenance',
            target: [{ reference: `GuidanceResponse/${existingResponse.id}` }],
            recorded: new Date().toISOString(),
            agent: profile ? [{ who: createReference(profile) }] : [],
            activity: { coding: [{ system: 'http://polarissleep.com/order-actions', code: 'amend' }] },
          })
        );
      }
      showNotification({ color: 'green', message: 'Referral status updated.' });
      onSaved();
      onClose();
    } catch (err) {
      Sentry.captureException(err);
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Update referral status" size="md">
      <Stack gap="md">
        <Text>
          Patient:{' '}
          <Anchor href={`/Patient/${entry?.patientId}`} target="_blank" rel="noopener noreferrer">
            {entry?.patientName}
          </Anchor>
        </Text>
        <Box bg="gray.0" p="md" style={{ borderRadius: 8 }}>
          <Stack gap="sm">
            <Select
              label="Referral Status"
              data={REFERRAL_STATUS_OPTIONS}
              value={status}
              onChange={(v) => {
                setStatus(v ?? PATIENT_REFERRAL_STATUS.na);
              }}
            />
            <Textarea
              label="Referral Notes"
              value={notes}
              onChange={(e) => {
                setNotes(e.currentTarget.value);
              }}
              maxLength={2000}
              autosize
              minRows={3}
            />
            <Textarea
              label="What happened?"
              description="Captured in the comms log and physician notes."
              required
              placeholder="e.g. Called lab to confirm study scheduled…"
              value={eventNote}
              onChange={(event) => setEventNote(event.currentTarget.value)}
              minRows={3}
            />
            <Group grow align="flex-start">
              <Select
                label="Contact method"
                data={[
                  { value: 'phone', label: 'Phone call' },
                  { value: 'fax', label: 'Fax' },
                  { value: 'email', label: 'Email (no PHI)' },
                  { value: 'portal', label: 'Portal' },
                ]}
                value={contactMethod}
                onChange={(value) => setContactMethod(value ?? 'phone')}
              />
              <TextInput
                label="Date & time"
                type="datetime-local"
                value={eventDateTime}
                onChange={(event) => setEventDateTime(event.currentTarget.value)}
              />
            </Group>
          </Stack>
        </Box>
        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canRefer || submitting} loading={submitting} onClick={handleSave}>
            Save & log update
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
